import * as React from "react";
import * as api from "app/shared/api";
import type { Centre } from "app/shared/model";
import type { EditPricelistResponse } from "app/shared/model/EditPricelist";
import { getDivisionId } from "../domain/storage/localStorage";
import { isNonFeeOffering } from "../domain/offering/guards";

export type AvailableProductOption = {
    id: number;
    name: string;
    unitTypeId?: number;
    unitTypeText?: string;
};

export interface UseAvailableProductsParams {
    visible: boolean;
    selectedProductType: string;
    selectedCentreCode?: string;
    centres: Centre[];
    draftPricelist?: EditPricelistResponse[];
    pricingYearAllowedStart?: string;
    pricingYearAllowedEnd?: string;
}

export interface UseAvailableProductsResult {
    availableProducts: AvailableProductOption[];
    loadingProducts: boolean;
    loadError: string | null;
}

export function useAvailableProducts(
    params: UseAvailableProductsParams
): UseAvailableProductsResult {
    const {
        visible,
        selectedProductType,
        selectedCentreCode,
        centres,
        draftPricelist,
        pricingYearAllowedStart,
        pricingYearAllowedEnd,
    } = params;

    const [availableProducts, setAvailableProducts] = React.useState<AvailableProductOption[]>([]);
    const [loadingProducts, setLoadingProducts] = React.useState(false);
    const [loadError, setLoadError] = React.useState<string | null>(null);

    //const divisionId = React.useMemo(() => getDivisionId(), [visible, selectedProductType]);
    const divisionId = getDivisionId();

    React.useEffect(() => {
        if (!visible) {
            setAvailableProducts([]);
            setLoadingProducts(false);
            setLoadError(null);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                setLoadingProducts(true);
                setLoadError(null);

                let products: any[] = [];

                switch (selectedProductType) {
                    case "Course":
                        products = divisionId
                            ? await (api as any).getCourseProducts(divisionId)
                            : [];
                        break;
                    case "Accommodation":
                        products = divisionId
                            ? await (api as any).getAccommodationRoomProductsByDivision(divisionId)
                            : [];
                        break;
                    case "AddOn":
                        products = divisionId
                            ? await (api as any).getAddonsProducts(divisionId)
                            : [];
                        break;
                    case "Transfer":
                        products = divisionId
                            ? await (api as any).getTransferProducts(divisionId)
                            : [];
                        break;
                    case "Package":
                        products = divisionId
                            ? await (api as any).getPackageProducts(divisionId)
                            : [];
                        break;
                    default:
                        products = [];
                }

                if (cancelled) return;

                const currentYearDraft = (pricingYearAllowedStart && pricingYearAllowedEnd)
                    ? (draftPricelist ?? []).filter((entry) => {
                        const startDate =
                            (entry as any).pricePeriods?.[0]?.startDate ??
                            (entry as any).dateStrategyAssignments?.[0]?.fromDate;
                        if (!startDate) return true;
                        const iso = typeof startDate === "string"
                            ? startDate.substring(0, 10)
                            : new Date(startDate as any).toISOString().substring(0, 10);
                        return iso >= pricingYearAllowedStart && iso <= pricingYearAllowedEnd;
                    })
                    : (draftPricelist ?? []);

                const existingKeys = new Set(
                    currentYearDraft
                        .filter(isNonFeeOffering)
                        .filter(
                            (entry) => entry?.productId?.type?.name === selectedProductType
                        )
                        .map((entry) => Number(entry?.productId?.id))
                        .filter((id) => Number.isFinite(id))
                        .map((id) => String(id))
                );

                const available = (products ?? [])
                    .filter((p) => {
                        const isActive =
                            p.active !== false &&
                            (p.active === true || !p.decommissionDate && !p.closureDate);

                        const pid = Number(p.id);

                        return isActive && !existingKeys.has(String(pid));
                    })
                    .map((p) => ({
                        id: Number(p.id),
                        name: String(p.name || p.productName || ""),
                        unitTypeId: p.unitTypeId != null ? Number(p.unitTypeId) : undefined,
                        unitTypeText: p.unitTypeText != null ? String(p.unitTypeText) : undefined,
                    }));

                setAvailableProducts(available);
            } catch (e: any) {
                console.warn("Failed to load products", e);
                if (!cancelled) {
                    setAvailableProducts([]);
                    setLoadError(String(e?.message ?? e));
                }
            } finally {
                if (!cancelled) setLoadingProducts(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        visible,
        selectedProductType,
        draftPricelist,
        divisionId,
        pricingYearAllowedStart,
        pricingYearAllowedEnd,
    ]);

    return { availableProducts, loadingProducts, loadError };
}